import type { AriaRole, MouseEventHandler } from 'react';

import Link from 'next/link';

import { ArrowRight } from 'lucide-react';

import { cn } from '@naumu/ui/utils/cn';

// ---------------------------------------------------------------------------
// arrow-link - the landing's one link grammar (Linear's bare-arrow form):
// semibold neutral lead word, optional grey tail, grey arrow that slides and
// brightens on hover. Used by the ledger stage pointers, the audience picker,
// the changelog heading row, and the nav popover footers - never re-implement
// the pattern inline. Margins/positioning come from the caller via className.
// ---------------------------------------------------------------------------

const SIZES = {
	/** Landing body links. */
	md: { root: 'gap-2 text-[14px]', arrow: 'size-4' },
	/** Dense rows (nav popover footer, inline sentence links). */
	sm: { root: 'gap-1.5 text-[12.5px]', arrow: 'size-3.5' },
} as const;

export interface ArrowLinkProps {
	href: string;
	/** The semibold neutral lead word(s). */
	label: string;
	/** Optional muted mono prefix before the label (spine numbers: "3.2"). */
	prefixLabel?: string;
	/** Optional muted tail after the label (e.g. "that doesn't walk out the door"). */
	secondaryLabel?: string;
	size?: keyof typeof SIZES;
	/** Force a plain anchor. Defaults to auto-detect (mailto:/http(s): hrefs). */
	external?: boolean;
	/**
	 * Open in a new tab. Defaults to auto: on for off-site http(s) hrefs (e.g.
	 * the Cal.com booking page), off for in-site paths and mailto:.
	 */
	newTab?: boolean;
	role?: AriaRole;
	className?: string;
	onClick?: MouseEventHandler<HTMLAnchorElement>;
}

export const ArrowLink = ({
	href,
	label,
	prefixLabel,
	secondaryLabel,
	size = 'md',
	external,
	newTab,
	role,
	className,
	onClick,
}: ArrowLinkProps) => {
	const rootClass = cn(
		'group inline-flex w-fit items-center outline-none focus-visible:ring-2 focus-visible:ring-accent',
		SIZES[size].root,
		className,
	);
	const content = (
		<>
			{prefixLabel && <span className="mk-type-index text-neutral/40">{prefixLabel}</span>}
			<span className="font-semibold text-neutral">{label}</span>
			{secondaryLabel && (
				<span className="text-neutral/40 transition-colors duration-200 group-hover:text-neutral/70">
					{secondaryLabel}
				</span>
			)}
			<ArrowRight
				className={cn(
					'text-neutral/35 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-neutral',
					SIZES[size].arrow,
				)}
			/>
		</>
	);
	const isExternal = external ?? /^(mailto:|https?:)/.test(href);
	const opensNewTab = newTab ?? /^https?:/.test(href);
	return isExternal ? (
		<a
			href={href}
			role={role}
			onClick={onClick}
			className={rootClass}
			{...(opensNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
		>
			{content}
		</a>
	) : (
		<Link href={href} role={role} onClick={onClick} className={rootClass}>
			{content}
		</Link>
	);
};
